import { ref, computed, onMounted, watch, nextTick, readonly, ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useChartStore } from '@/stores/chartStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useChartNotesV2 } from '@/composables/useChartNotesV2';
import { useLocalStorage } from '@/composables/useLocalStorage';
import { useDocumentProperties } from '@/composables/useDocumentProperties';
import { useModals } from '@/composables/useModals';
import { useScreenSizing } from '@/composables/useScreenSizing';
import { isValidNumber } from '@/utils/isValidNumber';
import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';
import { CHART_SIZE_MENU_OPTION_CUSTOM } from '@/enums/chart-size-menu-options';
import { CHART_IMAGE_CONTAINER_ID } from '@/enums/html-ids';

const CHART_IMAGE_CONTAINER_PADDING_TOP = 4;
const CHART_IMAGE_CONTAINER_PADDING_RIGHT = 12;
const CHART_IMAGE_CONTAINER_PADDING_BOTTOM = 12;
const CHART_IMAGE_CONTAINER_PADDING_LEFT = 4;
const CHART_STYLE_BUTTON_CONTAINER_WIDTH = 16;
const RESIZE_DRAGGING_HANDLE_DIAMETER = 14;
const WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART = CHART_IMAGE_CONTAINER_PADDING_LEFT;
const RESIZE_DRAGGING_PREVIEW_CSS_LEFT_ALIGNED_LEFT_VALUE = CHART_STYLE_BUTTON_CONTAINER_WIDTH + CHART_IMAGE_CONTAINER_PADDING_LEFT + WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART;
const RESIZE_DRAGGING_PREVIEW_CSS_CENTER_ALIGNED_LEFT_VALUE = Math.round((CHART_STYLE_BUTTON_CONTAINER_WIDTH + WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART) / 2);
const WIDTH_OF_CHART_NOTES_UI = 32;
const INVALID_SYMBOL_ERROR_SCREEN_WIDTH = 900;
const INVALID_SYMBOL_ERROR_SCREEN_HEIGHT = 400;
const ZOOM_THUMBNAIL_WIDTH_PIXELS = 150;

const CHART_DIMENSION_CONSTANTS = {
  CHART_IMAGE_CONTAINER_PADDING_TOP,
  CHART_IMAGE_CONTAINER_PADDING_RIGHT,
  CHART_IMAGE_CONTAINER_PADDING_BOTTOM,
  CHART_IMAGE_CONTAINER_PADDING_LEFT,
  CHART_STYLE_BUTTON_CONTAINER_WIDTH,
  RESIZE_DRAGGING_HANDLE_DIAMETER,
  WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART,
  RESIZE_DRAGGING_PREVIEW_CSS_LEFT_ALIGNED_LEFT_VALUE,
  RESIZE_DRAGGING_PREVIEW_CSS_CENTER_ALIGNED_LEFT_VALUE,
  WIDTH_OF_CHART_NOTES_UI,
  INVALID_SYMBOL_ERROR_SCREEN_WIDTH,
  INVALID_SYMBOL_ERROR_SCREEN_HEIGHT
};

const currentChartImageContainerYCoordinate = ref(0);

const currentCursorX = ref(0);
const currentCursorY = ref(0);

const IS_CHART_LEFT_ALIGNED_LOCAL_STORAGE_KEY = 'sc3WorkbenchIsChartLeftAligned';
const _isChartLeftAligned = ref(true);
const isChartLeftAligned = computed(() => {
  const { isLoggedIn } = useAuthentication();

  return _isChartLeftAligned.value || !isLoggedIn.value;
});

const IS_AUTO_SIZE_CHARTS_ENABLED_LOCAL_STORAGE_KEY = 'sc3WorkbenchIsAutoSizeChartsEnabled';
const isAutoSizeChartsEnabled = ref(true);

let isAutoSizeChartsCurrentlyActive: ComputedRef<boolean>;

const draggingToResizeChartWidthAndHeight = computed(() => {
  const { clientWidth } = useDocumentProperties();

  let chartWidth = 0;
  const chartImageContainerWidth = clientWidth.value - CHART_IMAGE_CONTAINER_PADDING_RIGHT - CHART_IMAGE_CONTAINER_PADDING_LEFT;
  const maxChartWidth = Math.floor(chartImageContainerWidth - CHART_STYLE_BUTTON_CONTAINER_WIDTH - WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART);

  if (isChartLeftAligned.value) {
    chartWidth = currentCursorX.value - RESIZE_DRAGGING_PREVIEW_CSS_LEFT_ALIGNED_LEFT_VALUE;
  } else {
    const chartImageContainerHalfway = (chartImageContainerWidth / 2) + CHART_IMAGE_CONTAINER_PADDING_LEFT;
    const chartImageMidpoint = chartImageContainerHalfway + ((CHART_STYLE_BUTTON_CONTAINER_WIDTH + WHITE_SPACE_BETWEEN_CHART_STYLE_BUTTONS_AND_CHART) / 2);
    chartWidth = 2 * Math.abs(chartImageMidpoint - currentCursorX.value);
  }

  chartWidth = Math.min(Math.round(chartWidth), maxChartWidth);
  const chartHeight = Math.round(currentCursorY.value - currentChartImageContainerYCoordinate.value);

  return {
    width: Math.max(1, chartWidth),
    height: Math.max(1, chartHeight)
  };
});

const resizeDraggingPreviewCSSStyles = computed(() => {
  const layoutStore = useLayoutStore();

  if (!layoutStore.isDraggingChartToResize) {
    return {};
  }

  const { width, height } = draggingToResizeChartWidthAndHeight.value;

  return {
    display: 'block',
    width: `${width}px`,
    height: `${height}px`,
  };
});

let chartImageContainerElement: HTMLElement | null = null;

let hasInitialized = false;

function getChartImageContainerElement(): HTMLElement | null {
  if (chartImageContainerElement) {
    return chartImageContainerElement;
  }

  chartImageContainerElement = document.getElementById(CHART_IMAGE_CONTAINER_ID);

  return chartImageContainerElement;
}

function isValidCustomWidth(customWidth: string | number): boolean {
  return isValidNumber(customWidth, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanZero: true });
}

function isValidCustomHeight(customHeight: string | number): boolean {
  return isValidNumber(customHeight, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanZero: true });
}

function setCurrentChartImageContainerYCoordinate(): void {
  const chartImageContainer = getChartImageContainerElement();

  if (!chartImageContainer) {
    return;
  }

  const { y } = chartImageContainer.getBoundingClientRect();

  currentChartImageContainerYCoordinate.value = y;
}

async function handleWindowResize(): Promise<void> {
  await waitForDomToRepaint();
  setCurrentChartImageContainerYCoordinate();
}

async function delayedWindowResizeHandler(): Promise<void> {
  await nextTick();
  await waitForDomToRepaint();
  await handleWindowResize();
}

function setIsChartLeftAligned(isChartNowLeftAligned: boolean): void {
  const { isLoggedIn } = useAuthentication();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { setLocalStorageData } = useLocalStorage();

  if (
    (!isChartNowLeftAligned && !isLoggedIn.value) ||
    isChartNowLeftAligned === _isChartLeftAligned.value ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value
  ) {
    return;
  }

  setLocalStorageData(IS_CHART_LEFT_ALIGNED_LOCAL_STORAGE_KEY, `${isChartNowLeftAligned}`);

  _isChartLeftAligned.value = isChartNowLeftAligned;
}

function setIsAutoSizeChartsEnabled(isAutoSizeChartsNowEnabled: boolean): void {
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { setLocalStorageData } = useLocalStorage();

  if (
    isAutoSizeChartsNowEnabled === isAutoSizeChartsEnabled.value ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value
  ) {
    return;
  }

  setLocalStorageData(IS_AUTO_SIZE_CHARTS_ENABLED_LOCAL_STORAGE_KEY, `${isAutoSizeChartsNowEnabled}`);

  isAutoSizeChartsEnabled.value = isAutoSizeChartsNowEnabled;
}

function handleResizeDraggingHandleMouseDown(e: MouseEvent): void {
  const layoutStore = useLayoutStore();

  layoutStore.$patch({ isDraggingChartToResize: true });
  setCurrentChartImageContainerYCoordinate();
  setResizeDraggingCursorCoordinates(e);
}

function setResizeDraggingCursorCoordinates(e: MouseEvent): void {
  currentCursorX.value = e.clientX;
  currentCursorY.value = e.clientY;
}

function handleMouseUp(e: MouseEvent): void {
  const layoutStore = useLayoutStore();

  if (!layoutStore.isDraggingChartToResize) {
    return;
  }

  window.setTimeout(() => {
    layoutStore.$patch({ isDraggingChartToResize: false });
    setResizeDraggingCursorCoordinates(e);
    submitNewDraggingToResizeChartDimensionsToChartStore();
  }, 0);
}

function handleMouseMove(e: MouseEvent): void {
  const layoutStore = useLayoutStore();

  if (!layoutStore.isDraggingChartToResize) {
    return;
  }

  setResizeDraggingCursorCoordinates(e);
}

function submitNewDraggingToResizeChartDimensionsToChartStore(): void {
  const { isLoggedIn } = useAuthentication();
  const { launchCreateAccountModal } = useModals();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  const { width, height } = draggingToResizeChartWidthAndHeight.value;

  submitChartDimensionsToChartStore(width, height);
}

function submitChartDimensionsToChartStore(width: number, height: number): void {
  const chartStore = useChartStore();
  let modifiedWidth = width;

  if (chartStore.zoomThumbnail) {
    modifiedWidth -= ZOOM_THUMBNAIL_WIDTH_PIXELS;
  }

  chartStore.$patch({
    chartSize: CHART_SIZE_MENU_OPTION_CUSTOM.title,
    customWidth: modifiedWidth,
    customHeight: height
  });
}

function init(): void {
  const layoutStore = useLayoutStore();
  const { addMemberAuthenticationCallback } = useAuthentication();
  const { getBooleanLocalStorageData } = useLocalStorage();
  const { ScreenSizes } = useScreenSizing();
  const { clientWidth } = useDocumentProperties();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();

  if (hasInitialized) {
    return;
  }

  hasInitialized = true;

  _isChartLeftAligned.value = getBooleanLocalStorageData(IS_CHART_LEFT_ALIGNED_LOCAL_STORAGE_KEY, true);

  const shouldTurnAutoSizeChartsOnByDefault = clientWidth.value < ScreenSizes.MediumDesktop;
  isAutoSizeChartsEnabled.value = getBooleanLocalStorageData(IS_AUTO_SIZE_CHARTS_ENABLED_LOCAL_STORAGE_KEY, shouldTurnAutoSizeChartsOnByDefault);

  isAutoSizeChartsCurrentlyActive = isAutoSizeChartsCurrentlyActive ?? computed(() => {
    return isAutoSizeChartsEnabled.value && !layoutStore.isInspecting && !(isActivatingAnnotationMode.value || isAnnotating.value);
  });

  const { isChartComponentMounted } = storeToRefs(layoutStore);

  window.addEventListener('resize', handleWindowResize);
  window.addEventListener('mouseup', handleMouseUp);
  window.addEventListener('mousemove', handleMouseMove);

  watch(isChartLeftAligned, delayedWindowResizeHandler);
  watch(isChartComponentMounted, delayedWindowResizeHandler);

  onMounted(delayedWindowResizeHandler);

  addMemberAuthenticationCallback(async () => {
    watch(isAutoSizeChartsCurrentlyActive, delayedWindowResizeHandler);

    await delayedWindowResizeHandler();
  });
}

export function useChartDimensions() {
  init();

  return {
    CHART_DIMENSION_CONSTANTS: readonly(CHART_DIMENSION_CONSTANTS),
    isChartLeftAligned,
    isAutoSizeChartsEnabled: readonly(isAutoSizeChartsEnabled),
    isAutoSizeChartsCurrentlyActive,
    resizeDraggingPreviewCSSStyles,
    isValidCustomWidth,
    isValidCustomHeight,
    setIsChartLeftAligned,
    setIsAutoSizeChartsEnabled,
    handleResizeDraggingHandleMouseDown
  };
}
